import './PurchaseTable.scss';

import { type FC, useState, useMemo } from 'react';
import classNames from 'classnames';
import { uniqBy } from 'lodash-es';

import { FontLicenseType } from 'client/graphql/types/operations';

import IndividualStyles from './IndividualStyles';
import PackageCell from './PackageCell';
import SubPackages from './SubPackages';
import StyleDetails from './StyleDetails';
import { usePurchaseTableLayout } from './utils/usePurchaseTableLayout';
import AddToCartButton from '../AddToCartButton';
import * as cellSizing from './utils/cellSizing';
import { sortFontFaces } from 'utils/sorting';

interface Props {
  family: {
    id: number;
    name: string;
    conceptFont: boolean;
    completePrice: number;
    fontFacePrice: number;
    subPackageFontFacePrice: number;
    discountedFontFacePrice: number;
    hasWidthVariableFiles: boolean;
    faces: {
      id: number;
      width: { slug: string; name: string; position: number };
      size: { slug: string; name: string; position: number };
      weight: { slug: string; name: string; position: number };
      style: { slug: string; name: string; position: number };
      description: string;
      familyPreview: { url: string | null };
      singlePreview: { url: string | null };
      singleCharacterPreview: { url: string | null };
      family: { fontFacePrice: number; discountedFontFacePrice: number };
    }[];
  };
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
}

const PurchaseTable: FC<Props> = (props) => {
  const { family, licenseOption } = props;

  const { packagesStacked } = usePurchaseTableLayout();

  const [hoveredFaceId, setHoveredFaceId] = useState<number | null>(null);

  const [hoveredPackageFaceIds, setHoveredPackageFaceIds] = useState<number[]>(
    [],
  );

  const hoveredFace = hoveredFaceId
    ? family.faces.find((face) => face.id === hoveredFaceId)
    : null;

  const sortedFaces = useMemo(
    () => sortFontFaces(family.faces),
    [family.faces],
  );

  const familyWidths = useMemo(
    () =>
      uniqBy(
        sortedFaces.map((face) => face.width),
        (width) => width.slug,
      ),
    [sortedFaces],
  );

  const familyWeights = useMemo(
    () =>
      uniqBy(
        sortedFaces.map((face) => face.weight),
        (weight) => weight.slug,
      ),
    [sortedFaces],
  );

  const familyStyles = useMemo(
    () =>
      uniqBy(
        sortedFaces.map((face) => face.style),
        (style) => style.slug,
      ),
    [sortedFaces],
  );

  const fullFamilyOnly =
    family.conceptFont || licenseOption?.type === FontLicenseType.Student;

  return (
    <div
      className={classNames('PurchaseTable', {
        'is-fullFamilyOnly': fullFamilyOnly,
        'is-disabled': licenseOption?.type == null,
      })}
      style={{
        gridTemplateColumns: packagesStacked ? `0 1fr 0` : undefined,
        width: packagesStacked ? `100%` : undefined,
      }}
    >
      <div
        className={classNames('PurchaseTable-completeFamilyPackageCell', {
          'PurchaseTable-completeFamilyPackageCell--packagesStacked':
            packagesStacked,
        })}
        style={{
          width: !packagesStacked
            ? `calc(${cellSizing.cellSize} * ${familyWeights.length})`
            : undefined,
        }}
      >
        <PackageCell
          licenseOption={licenseOption}
          family={family}
          faces={family.faces}
          complete
          onHover={setHoveredPackageFaceIds}
        />
      </div>

      <div className="PurchaseTable-individualStylesCell">
        {packagesStacked ? (
          <div
            className={classNames(
              'PurchaseTable-individualStylesCell-stacked',
              {
                'PurchaseTable-individualStylesCell-stacked--singleStyle':
                  familyStyles.length === 1,
              },
            )}
          >
            {sortedFaces.map((fontFace) => (
              <div
                className="PurchaseTable-individualStylesCell-stacked-individualStyle"
                key={fontFace.id}
              >
                <img
                  src={fontFace.singlePreview.url ?? undefined}
                  className="PurchaseTable-individualStylesCell-stacked-individualStyle-image"
                  loading="lazy"
                  decoding="async"
                />

                <AddToCartButton
                  family={family}
                  faces={[fontFace]}
                  licenseOption={licenseOption}
                  unavailable={fullFamilyOnly}
                />
              </div>
            ))}
          </div>
        ) : (
          <IndividualStyles
            family={family}
            licenseOption={licenseOption}
            disabled={fullFamilyOnly}
            hoveredPackageFaceIds={hoveredPackageFaceIds}
            onHoverFace={(faceId) => setHoveredFaceId(faceId)}
          />
        )}
      </div>

      <div
        className={classNames('PurchaseTable-packagesCell', {
          'PurchaseTable-packagesCell--packagesStacked': packagesStacked,
        })}
      >
        {familyWidths.length > 1 && (
          <SubPackages
            licenseOption={licenseOption}
            family={family}
            faces={family.faces}
            disabled={fullFamilyOnly}
            onHover={(ids) => setHoveredPackageFaceIds(ids)}
          />
        )}
      </div>

      {packagesStacked === false && (
        <>
          <div className="PurchaseTable-weightLabelsCell">
            {familyWeights.map((weight) => (
              <div
                key={weight.slug}
                className="PurchaseTable-weightLabelsCell-label"
                style={{
                  width: `calc(${cellSizing.cellSize})`,
                }}
              >
                {weight.name}
              </div>
            ))}
          </div>

          <div className="PurchaseTable-uprightItalicLabelsCell">
            {familyStyles.length > 1 &&
              familyWidths.map((width) => {
                const widthStyles = uniqBy(
                  sortedFaces
                    .filter((face) => face.width === width)
                    .map((face) => face.style),
                  (style) => style.slug,
                );

                return (
                  <div key={width.slug}>
                    {widthStyles.map((style) => (
                      <div
                        key={style.slug}
                        className={classNames(
                          'PurchaseTable-uprightItalicLabelsCell-label',
                          { 'is-italic': style.slug === 'italic' },
                        )}
                        style={{
                          height:
                            // TODO: This logic works for Plastique, but it
                            // should be improved to handle any family with a
                            // different number of styles per width
                            familyStyles.length > widthStyles.length
                              ? `calc(${cellSizing.cellSize} * 1.6)`
                              : cellSizing.cellSize,
                        }}
                      >
                        {style.name}
                      </div>
                    ))}
                  </div>
                );
              })}
          </div>
        </>
      )}

      {packagesStacked !== true && (
        <div className="PurchaseTable-hoveredItemDetails">
          {hoveredFace && (
            <StyleDetails
              family={family}
              face={hoveredFace}
              licenseOption={licenseOption}
              disabled={fullFamilyOnly}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default PurchaseTable;
